import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { anonymousJoin } from "../lib/anonymousJoin";
import { clearPendingJoin, readPendingJoin } from "../lib/pendingJoin";

/**
 * Pantalla 0-tap: cuando el cliente escanea el QR del local (URL `/?join=<slug>`),
 * o entra a `/j/<slug>`, el flujo es:
 *   1) Muestra un splash con el nombre del tenant (o un loader simple).
 *   2) Crea sesión anónima invisible.
 *   3) Llama joinTenant.
 *   4) Redirige a /.
 *
 * Sin login. Sin tap. El usuario ya está dentro sumando tickets en <3 segundos.
 */
export function AutoJoin() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { refreshClaims } = useAuth();
  const tenantId = params.get("join") ?? readPendingJoin();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!tenantId) {
      nav("/login", { replace: true });
      return;
    }
    let cancelled = false;
    (async () => {
      const result = await anonymousJoin(tenantId);
      if (cancelled) return;
      if (result.ok) {
        clearPendingJoin();
        await refreshClaims();
        nav("/", { replace: true });
      } else {
        setError(messageFor(result.error));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <main className="min-h-dvh bg-cream">
      <div className="container-page flex flex-col items-center gap-6 text-center">
        <div className="grid h-16 w-16 place-items-center rounded-full bg-leaf-50 ring-1 ring-leaf-100">
          <LeafMark />
        </div>
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-leaf-600">Fideliza</p>
          {!error ? (
            <>
              <h1 className="mt-2 font-display text-2xl font-semibold text-leaf-950">
                Conectando…
              </h1>
              <p className="mt-2 text-sm text-leaf-700">
                En segundos estarás sumando tickets.
              </p>
              <div className="mt-6 flex justify-center">
                <Spinner />
              </div>
            </>
          ) : (
            <>
              <h1 className="mt-2 font-display text-2xl font-semibold text-leaf-950">
                No pudimos conectarte
              </h1>
              <p className="mt-2 text-sm text-leaf-700">{error}</p>
              <button onClick={() => nav("/login", { replace: true })} className="btn-primary mt-6">
                Ir al inicio de sesión
              </button>
            </>
          )}
        </div>
      </div>
    </main>
  );
}

function LeafMark() {
  return (
    <svg viewBox="0 0 64 64" className="h-9 w-9" aria-hidden="true">
      <path
        d="M32 12c-10 9-14 18-14 25 0 9 7 15 14 15s14-6 14-15c0-7-4-16-14-25z"
        fill="#618728"
      />
      <path
        d="M32 20c-3 5-3 11 0 17"
        stroke="#ffffff"
        strokeWidth="2.5"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

function Spinner() {
  return (
    <svg className="h-6 w-6 animate-spin text-leaf-600" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.2" />
      <path
        d="M22 12a10 10 0 0 1-10 10"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
}

function messageFor(code: string | undefined): string {
  if (!code) return "Algo salió mal. Inténtalo de nuevo.";
  if (code.includes("not-found")) return "Ese comercio no existe o no está activo.";
  if (code.includes("failed-precondition"))
    return "Ya estás asociado a otro comercio. Cierra sesión e inténtalo de nuevo.";
  return "No pudimos conectarte al comercio.";
}
